import React, { ReactNode, useCallback } from 'react'
import { useKey } from 'react-use'
import {
  usePrototypeLayout,
  usePrototypeResizeMode,
} from '@sketch-hq/sketch-prototype-player'
import { IS_EMBEDDED } from '@sketch/constants'

import { useResponsiveDropdown } from '@sketch/components'
import * as S from './PrototypeResizeModeSelector.styles'
import { keyWithoutModifier, keyCodeWithShift } from '@sketch/utils'

export function PrototypeResizeModeSelector() {
  const { setResizeMode } = usePrototypeResizeMode()
  const layout = usePrototypeLayout()
  const prototypeScalePercent = Math.round(layout.scale * 100)

  const setToActualSize = useCallback(() => setResizeMode('actual-size'), [
    setResizeMode,
  ])

  const setToFitBoth = useCallback(() => setResizeMode('fit-both'), [
    setResizeMode,
  ])

  const setToFitWidth = useCallback(() => setResizeMode('fit-width'), [
    setResizeMode,
  ])

  useKey(keyWithoutModifier('z'), setToActualSize)
  useKey(keyCodeWithShift('Digit0'), setToFitBoth)
  useKey(keyCodeWithShift('Digit1'), setToFitWidth)

  const [content, buttonProps] = useResponsiveDropdown({
    dropdown: ResizeModeDropdown,
    dropdownProps: {
      setToActualSize,
      setToFitBoth,
      setToFitWidth,
    },
    placement: 'bottom-end',
    usePortal: true,
  })

  return (
    <>
      <S.ResizeModeSelectorButton
        variant="strong"
        label={`${prototypeScalePercent}%`}
        fullWidth
        data-testid="resize-mode-selector"
        chevrons
        {...buttonProps}
      />
      {content}
    </>
  )
}

type ResizeModeDropdownProps = {
  setToActualSize: () => void
  setToFitBoth: () => void
  setToFitWidth: () => void
}

function ResizeModeDropdown(props: ResizeModeDropdownProps) {
  const { resizeMode } = usePrototypeResizeMode()
  return (
    <>
      <ResizeModeDropdownItem
        onClick={props.setToActualSize}
        active={resizeMode === 'actual-size'}
        keyboardShortCutHint="z"
      >
        Actual Size
      </ResizeModeDropdownItem>
      <ResizeModeDropdownItem
        onClick={props.setToFitBoth}
        active={resizeMode === 'fit-both'}
        keyboardShortCutHint="Shift 0"
      >
        Fit Canvas
      </ResizeModeDropdownItem>
      <ResizeModeDropdownItem
        onClick={props.setToFitWidth}
        active={resizeMode === 'fit-width'}
        keyboardShortCutHint="Shift 1"
      >
        Fit Canvas Width
      </ResizeModeDropdownItem>
    </>
  )
}

type ResizeModeDropdownItemProps = {
  active: boolean
  children: ReactNode
  onClick: () => void
  keyboardShortCutHint: string
}

function ResizeModeDropdownItem(props: ResizeModeDropdownItemProps) {
  return (
    <S.Item onClick={props.onClick}>
      <S.StyledRadioButton
        checked={props.active}
        // noop to avoid console warning (we are using onClick handler instead)
        onChange={() => {}}
      />
      <S.Label>{props.children}</S.Label>
      {!IS_EMBEDDED && (
        <S.KeyboardShortcut>{props.keyboardShortCutHint}</S.KeyboardShortcut>
      )}
    </S.Item>
  )
}
